import { Subject } from 'rxjs';
import { Person } from '../models/person.model';
import { Walt } from '@weavix/models/src/walt/walt';
import { MapWalt } from '../models/weavix-map.model';
import { Utils } from '../utils/utils';
import { PersonService } from './person.service';
import { StateServiceBase, StateUpdate, StateUpdateType } from './state-base.service';
import { WaltService } from './walt.service';
import { ProfileService } from './profile.service';
import { PermissionAction } from '@weavix/models/src/permission/permissions.model';

export class WaltStateService extends StateServiceBase {
    private people = new Map<string, Person>();
    private readonly personToWaltMap = new Map<string, MapWalt>();
    private lastLoadArgs: { accountId: string, facilityId?: string, tags?: string[] } | null = null;
    /** Site the store is limited to, set when subscriptions start; unset means walts from every permitted site are kept. */
    private scopedFacilityId?: string;

    walts = new Map<string, MapWalt>();
    walts$ = new Subject<StateUpdate<Walt>>();

    constructor(
        private readonly waltService: WaltService,
        private readonly personService: PersonService,
        private readonly profileService: ProfileService,
    ) {
        super();
    }

    private addPersonToWalt(w: MapWalt): MapWalt {
        w = { ...w, person: this.people.get(w.lastAssigned.personId) };
        this.personToWaltMap.set(w.lastAssigned.personId, w);
        return w;
    }

    async loadData(c: any, accountId: string, facilityId?: string, tags?: string[]) {
        this.lastLoadArgs = { accountId, facilityId, tags };
        const allWithPerson = await this.fetchWalts(c, accountId, facilityId);
        this.walts = new Map(Object.entries(allWithPerson));
    }

    async refresh(c: any): Promise<void> {
        if (!this.lastLoadArgs) return;
        const { accountId, facilityId } = this.lastLoadArgs;
        const allWithPerson = await this.fetchWalts(c, accountId, facilityId);

        Array.from(this.walts.keys()).forEach(id => {
            if (!allWithPerson[id]) {
                const item = this.walts.get(id);
                this.walts.delete(id);
                this.walts$.next({ data: item, type: StateUpdateType.Delete });
            }
        });

        Object.keys(allWithPerson).forEach(id => {
            const fresh = allWithPerson[id];
            const existing = this.walts.get(id);

            if (existing) {
                fresh.marker = existing.marker;
                fresh.listener = existing.listener;
                fresh.closeListener = existing.closeListener;
            }

            const type = existing ? StateUpdateType.Update : StateUpdateType.Add;

            this.walts.set(id, fresh);
            this.walts$.next({ data: fresh, type });
        });
    }

    private async fetchWalts(c: any, accountId: string, facilityId?: string): Promise<{ [id: string]: MapWalt }> {
        const allPeople = await this.personService.getPeople(c);
        this.people = Utils.toMap(allPeople);
        const all = !facilityId ? await this.waltService.getAllByAccount(c, accountId)
            : await this.waltService.getAllByFacility(c, facilityId);

        const allWithPerson: { [id: string]: MapWalt } = {};
        all.filter(w => this.profileService.hasFacilitiesPermission(PermissionAction.ViewDeviceManagement, w.facilityId))
            .forEach(w => {
                let walt: any = w;
                if (w.lastAssigned?.personId) walt = this.addPersonToWalt(w);
                allWithPerson[w.id] = walt;
            });
        return allWithPerson;
    }

    async stop() {
        super.stop();
        this.walts.clear();
    }

    /**
     * Merges an incoming walt record into the store, normalizing nullable fields. Drops it, removing any stored copy, when it is
     * assigned outside the scoped site or the caller no longer has device-management visibility on its facility.
     */
    private applyWaltUpdate(payload: MapWalt): void {
        if (this.scopedFacilityId && payload.facilityId !== this.scopedFacilityId) {
            if (this.walts.has(payload.id)) this.updateFromMap(this.walts, { [payload.id]: null }, this.walts$);
            return;
        }
        let w: MapWalt = payload;
        if (w.lastAssigned?.personId) w = this.addPersonToWalt(w);
        if (!w.loggedIn) {
            const currWalt = this.walts.get(w.id);
            if (currWalt) currWalt.loggedIn = null;
        }
        if (!w.poweredOff) w.poweredOff = null;
        if (!w.lastAssigned) w.lastAssigned = null;
        if (!w.badge) w.badge = null;
        if (!w.craft) w.craft = null;
        if (!w.meshRepeater) w.meshRepeater = null;
        if (!this.profileService.hasFacilitiesPermission(PermissionAction.ViewDeviceManagement, w.facilityId)) {
            this.updateFromMap(this.walts, { [w.id]: null }, this.walts$);
        } else this.updateFromMap(this.walts, { [w.id]: w }, this.walts$);
    }

    /**
     * Applies a manual-refresh HTTP response to the store, using the same merge and permission
     * handling as the MQTT update path. Ignored when the store already holds a strictly newer record.
     */
    applyRefreshedWalt(walt: MapWalt): void {
        const existing = this.walts.get(walt.id);
        const existingUpdatedAt = existing ? new Date(existing.updated ?? 0).getTime() : 0;
        const incomingUpdatedAt = new Date(walt.updated ?? 0).getTime();
        if (existing && existingUpdatedAt > incomingUpdatedAt) return;
        this.applyWaltUpdate({ ...walt });
    }

    protected async setupSubscriptions(c: any, accountId: string, facilityId?: string, tags?: string[]) {
        this.scopedFacilityId = facilityId;
        const subs = [
            (await this.waltService.subscribeWaltUpdates(c)).subscribe((result) => {
                this.applyWaltUpdate(result.payload);
            }),
            (await this.personService.subscribePersonUpdates(c)).subscribe(async (result) => {
                const person = result.payload;
                this.people.set(person.id, person);
                const personWalt = this.personToWaltMap.get(person.id);
                if (personWalt && this.walts.has(personWalt.id)) {
                    personWalt.person = person;
                    this.updateFromMap(this.walts, { [personWalt.id]: personWalt }, this.walts$);
                }
            }),
        ];
        return subs;
    }
}
